/**
 * Decode HTML entities in a plain-text value (ENG-4305).
 *
 * Deliberately a small table rather than a library. `he` costs 33KB gzipped and is not
 * tree-shakeable, and `entities` costs 20KB and decodes its trie through a global `atob`
 * that jsdom rejects. This runs on every resident page load, so neither is a fair price
 * for repairing entity-encoded text.
 *
 * Pure string work, no DOM. A `document.createElement('textarea')` decoder returns its
 * input untouched on the server, and EPF server-renders content, so a DOM-based decoder
 * yields `&amp;` in the SSR HTML and `&` after hydration: a hydration mismatch and a
 * visible flash rather than a fix.
 *
 * SAFETY: only call this on values that land in a React text node or a text-only attribute,
 * both of which escape on output, so decoding cannot produce markup. Never call it on a
 * value bound for a markdown or `dangerouslySetInnerHTML` sink: stored `&lt;script&gt;` is
 * inert text today, and decoding it there would emit a live `<script>` into the
 * server-rendered document. The one exception is a value that is immediately re-escaped
 * before it reaches that sink: `safeLinks.ts` decodes a raw tag's `href` attribute only to
 * check and rebuild it through its own attribute escaper, so the decoded text itself never
 * reaches the page.
 *
 * Two deliberate divergences from the HTML spec, both safer for author text than for markup:
 * a trailing semicolon is required, so `Rock & Roll` and a literal `&notarealentity;` are
 * left intact rather than partially expanded via the legacy `&not` reference; and a
 * reference outside the table is left as written rather than guessed at. Unknown input is
 * therefore never mangled, only left as it renders today.
 */

/**
 * What the write-path sanitizer emitted, plus what arrives with content migrated from
 * Commonplace. Not the full 2000-entry HTML table: anything absent is left alone, so adding
 * an entry is a safe, additive change when real content turns one up.
 */
const NAMED_ENTITIES: Record<string, string> = {
  // The sanitizer's own output, and the cause of the reported damage.
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  // Typography, which arrives from pasted and migrated copy.
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  sbquo: '‚',
  bdquo: '„',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  bull: '•',
  middot: '·',
  laquo: '«',
  raquo: '»',
  iexcl: '¡',
  iquest: '¿',
  // Symbols and currency.
  copy: '©',
  reg: '®',
  trade: '™',
  deg: '°',
  plusmn: '±',
  times: '×',
  divide: '÷',
  frac12: '½',
  frac14: '¼',
  frac34: '¾',
  sup2: '²',
  sup3: '³',
  micro: 'µ',
  para: '¶',
  sect: '§',
  pound: '£',
  euro: '€',
  yen: '¥',
  cent: '¢',
  // Accented letters, for place and person names. Both cases: `Ñ` and `É` start words.
  agrave: 'à',
  aacute: 'á',
  acirc: 'â',
  atilde: 'ã',
  auml: 'ä',
  aring: 'å',
  aelig: 'æ',
  ccedil: 'ç',
  egrave: 'è',
  eacute: 'é',
  ecirc: 'ê',
  euml: 'ë',
  igrave: 'ì',
  iacute: 'í',
  icirc: 'î',
  iuml: 'ï',
  ntilde: 'ñ',
  ograve: 'ò',
  oacute: 'ó',
  ocirc: 'ô',
  otilde: 'õ',
  ouml: 'ö',
  oslash: 'ø',
  ugrave: 'ù',
  uacute: 'ú',
  ucirc: 'û',
  uuml: 'ü',
  yacute: 'ý',
  yuml: 'ÿ',
  szlig: 'ß',
  Agrave: 'À',
  Aacute: 'Á',
  Acirc: 'Â',
  Atilde: 'Ã',
  Auml: 'Ä',
  Aring: 'Å',
  AElig: 'Æ',
  Ccedil: 'Ç',
  Egrave: 'È',
  Eacute: 'É',
  Ecirc: 'Ê',
  Euml: 'Ë',
  Igrave: 'Ì',
  Iacute: 'Í',
  Icirc: 'Î',
  Iuml: 'Ï',
  Ntilde: 'Ñ',
  Ograve: 'Ò',
  Oacute: 'Ó',
  Ocirc: 'Ô',
  Otilde: 'Õ',
  Ouml: 'Ö',
  Oslash: 'Ø',
  Ugrave: 'Ù',
  Uacute: 'Ú',
  Ucirc: 'Û',
  Uuml: 'Ü',
  Yacute: 'Ý',
};

const REPLACEMENT_CHARACTER = '�';
const MAX_CODE_POINT = 0x10ffff;
const SURROGATE_START = 0xd800;
const SURROGATE_END = 0xdfff;

const ENTITY_PATTERN = /&(#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g;

/**
 * Per the HTML spec, NUL, lone surrogates and out-of-range code points decode to U+FFFD.
 * That matters beyond correctness: a lone surrogate cannot be UTF-8 encoded, so letting one
 * through would corrupt the SSR response body.
 */
const decodeNumeric = (reference: string): string => {
  const isHex = reference[1] === 'x' || reference[1] === 'X';
  const codePoint = parseInt(reference.slice(isHex ? 2 : 1), isHex ? 16 : 10);
  if (codePoint === 0 || codePoint > MAX_CODE_POINT) return REPLACEMENT_CHARACTER;
  if (codePoint >= SURROGATE_START && codePoint <= SURROGATE_END) return REPLACEMENT_CHARACTER;
  return String.fromCodePoint(codePoint);
};

export function decodeHtmlEntities(input: string): string {
  // Translations arrive as untyped JSON and several callers reach them through `as never`,
  // so a non-string can turn up here despite the signature. Guard rather than throw.
  if (typeof input !== 'string' || !input) return input;

  // One pass. `replace` never rescans what it has written, so the literal text `&amp;lt;`
  // decodes to `&lt;` rather than being expanded twice into `<`.
  return input.replace(ENTITY_PATTERN, (match, reference: string) => {
    if (reference.startsWith('#')) return decodeNumeric(reference);
    return NAMED_ENTITIES[reference] ?? match;
  });
}
